"use client";

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PricingSection } from '../components/PlansSection';
import FAQSection from '../components/FAQSection';
import {
  ArrowUpRight,
  Terminal,
  Menu,
  X,
  ChevronRight,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export const LandingPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  
  // Rastreio de Progresso do Scroll para transições suaves
  const { scrollYProgress } = useScroll();
  const scaleProgress = useTransform(scrollYProgress, [0, 0.2], [1, 0.96]);

  const steps = [
    { 
      id: "01",
      label: "Núcleo POS & Faturação", 
      headline: "Vendas sem atrito visual ou operacional.",
      sub: "Registe vendas, emita documentos comerciais (faturas, recibos e VD) e gira o seu caixa com velocidade máxima e integração M-Pesa.",
      image: "https://meupontodevenda.com/cdn/pos.png",
      tag: "TEMPO REAL"
    },
    { 
      id: "02",
      label: "Portal Público 24/7", 
      headline: "A sua infraestrutura digital aberta e a faturar.",
      sub: "Transforme o catálogo da sua empresa numa loja online integrada sem escrever código. Receba pedidos e pagamentos diretamente.",
      image: "https://meupontodevenda.com/cdn/portal.gif",
      tag: "LOJA ONLINE"
    },
    { 
      id: "03",
      label: "Motor de Metas & Performance", 
      headline: "Gestão comercial orientada por objetivos.",
      sub: "Definição de metas por vendedor, acompanhamento do progresso em tempo real e cálculo automatizado de comissões.",
      image: "https://meupontodevenda.com/cdn/goal.gif",
      tag: "PERFORMANCE"
    },
    { 
      id: "04",
      label: "Inteligência de Rentabilidade", 
      headline: "Decisões comerciais com dados concretos.",
      sub: "Análise de margem de contribuição, curva ABC de produtos e rentabilidade real por cliente e canal de venda.",
      image: "https://meupontodevenda.com/cdn/report.gif",
      tag: "UNIT ECONOMICS"
    }
  ];

  useEffect(() => {
    // Animação do grid de fundo técnico
    const ctx = gsap.context(() => {
      gsap.to(".grid-line-v", {
        scaleY: 1,
        duration: 1.5,
        stagger: 0.1,
        ease: "power4.out"
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0C1E] text-[#e2e8f0] font-sans antialiased selection:bg-[#52C47C] selection:text-[#0B0C1E] overflow-x-hidden">
      
      {/* Structural Grid Systems - Design Técnico */}
      <div className="fixed inset-0 pointer-events-none z-0 flex justify-between px-4 sm:px-12 opacity-[0.03]">
        <div className="w-[1px] h-full bg-white grid-line-v scale-y-0 origin-top" />
        <div className="w-[1px] h-full bg-white grid-line-v scale-y-0 origin-top" />
        <div className="w-[1px] h-full bg-white grid-line-v scale-y-0 origin-top" />
      </div>

      {/* BANNER DE CAMPANHA */}
      <div className="bg-[#52C47C] text-[#0B0C1E] text-[11px] sm:text-xs font-mono font-bold tracking-wider py-2.5 px-4 text-center border-b border-black/10 flex items-center justify-center gap-3 relative z-50">
        <span className="flex h-2 w-2 rounded-full bg-[#0B0C1E] animate-ping" />
        <span>FOUNDERS LIFETIME PASS — Desde 39.900 MT · Apenas 15 empresas · Sem mensalidade para sempre</span>
        <a href="#planos" className="underline font-black hover:opacity-80 transition uppercase ml-2">Ver Planos →</a>
      </div>

      {/* HEADER / NAVIGATION */}
      <header className="sticky top-0 z-40 bg-[#0B0C1E]/85 backdrop-blur-xl border-b border-white/10 px-6 sm:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-7 w-auto object-contain transition-transform group-hover:scale-95"
            />
            <span className="hidden xl:inline-block font-mono text-[10px] tracking-widest text-[#8087A8] border-l border-white/10 pl-3">
              SISTEMA v4.0
            </span>
          </Link>
        </div>

        <nav className="hidden lg:flex items-center gap-8 font-mono text-xs tracking-wider text-slate-300">
          <a href="#arquitetura" className="hover:text-[#52C47C] transition-colors">01 // VISÃO</a>
          <a href="#demostracao" className="hover:text-[#52C47C] transition-colors">02 // ECOSSISTEMA</a>
          <a href="#funcionalidades" className="hover:text-[#52C47C] transition-colors">03 // SOLUÇÕES</a>
          <a href="#ecossistema" className="hover:text-[#52C47C] transition-colors">04 // VALOR</a>
          <a href="#captacao" className="hover:text-[#52C47C] transition-colors">05 // CAPTAÇÃO</a>
          <a href="#planos" className="hover:text-[#52C47C] transition-colors">06 // LIFETIME PASS</a>
          <a href="#faq" className="hover:text-[#52C47C] transition-colors">07 // FAQ</a>
        </nav>

        <div className="flex items-center gap-4 font-mono">
          <Link to="/login" className="hidden sm:inline-block text-xs uppercase tracking-widest text-slate-300 hover:text-white px-3 py-2 transition">
            Entrar
          </Link>
          <a
            href="#planos"
            className="px-5 py-2.5 bg-[#52C47C] text-[#0B0C1E] font-bold text-xs tracking-wider uppercase hover:bg-white transition-all flex items-center gap-2 group shadow-lg shadow-[#52C47C]/15"
          >
            <span>Ver Planos</span>
            <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <button className="lg:hidden text-white ml-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      {/* MOBILE MENU OVERLAY */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-30 bg-[#0B0C1E] p-8 flex flex-col justify-between font-mono lg:hidden border-b border-white/10 pt-24">
          <nav className="flex flex-col gap-6 text-lg tracking-widest">
            <a href="#arquitetura" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">01 // VISÃO</a>
            <a href="#demostracao" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">02 // ECOSSISTEMA</a>
            <a href="#funcionalidades" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">03 // SOLUÇÕES</a>
            <a href="#ecossistema" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">04 // VALOR</a>
            <a href="#captacao" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">05 // CAPTAÇÃO</a>
            <a href="#planos" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">06 // LIFETIME PASS</a>
            <a href="#faq" onClick={() => setIsMenuOpen(false)} className="text-slate-300 hover:text-[#52C47C]">07 // FAQ</a>
          </nav>
          <div className="pt-8 border-t border-white/10 flex flex-col gap-4">
            <Link to="/login" className="text-center py-3 border border-white/20 text-xs uppercase tracking-widest">Aceder à Conta</Link>
            <a href="#planos" className="text-center py-3 bg-[#52C47C] text-[#0B0C1E] text-xs font-bold uppercase tracking-widest">Ver Planos</a>
          </div>
        </div>
      )}

      {/* SECTION 1: HERO */}
      <motion.section 
        id="arquitetura"
        style={{ scale: scaleProgress }} 
        ref={heroRef} 
        className="relative pt-20 pb-28 sm:pt-32 sm:pb-40 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10"
      >
        <div className="flex flex-col items-start text-left">
          
          <div className="inline-flex items-center gap-3 px-3 py-1.5 border border-[#5055A8]/40 bg-[#5055A8]/10 font-mono text-xs text-[#52C47C] tracking-widest uppercase mb-8">
            <Terminal className="w-3.5 h-3.5" />
            <span>SISTEMA DE GESTÃO COMERCIAL — MOÇAMBIQUE</span>
          </div>

          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-light tracking-tight text-white max-w-5xl leading-[0.98] mb-10">
           A sua empresa vende.<span className="font-normal italic text-[#52C47C]">O MPV organiza o resto.</span>
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 w-full items-end mt-4">
            <p className="lg:col-span-7 text-lg sm:text-xl text-slate-300 font-light leading-relaxed">
              Do primeiro contacto ao pagamento, o <strong className="text-white font-medium">MPV — Meu Ponto de Venda</strong> conecta leads, clientes, vendas, stock, facturação e rentabilidade numa única plataforma, criada para a realidade das empresas moçambicanas.
            </p>

            <div className="lg:col-span-5 flex flex-col sm:flex-row gap-4 lg:justify-end">
              <a 
                href="#planos" 
                className="px-8 py-5 bg-[#52C47C] text-[#0B0C1E] font-mono text-xs font-bold tracking-widest uppercase hover:bg-white transition-all flex items-center justify-center gap-3 group shadow-xl shadow-[#52C47C]/20"
              >
                <span>EXPLORAR O MPV</span>
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

     {/* MÉTRICAS E DIFERENCIAIS */}
<div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 w-full mt-24 pt-12 border-t border-white/10 font-mono">

  {/* VELOCIDADE */}
  <div>
    <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mb-3">
      // VELOCIDADE
    </p>

    <p className="text-4xl sm:text-5xl font-light tracking-tight text-white">
      3s
    </p>

    <p className="text-xs text-slate-400 leading-relaxed mt-2 max-w-[220px]">
      Venda em apenas três cliques.
    </p>
  </div>

  {/* DISPONIBILIDADE */}
  <div>
    <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mb-3">
      // DISPONIBILIDADE
    </p>

    <p className="text-4xl sm:text-5xl font-light tracking-tight text-[#52C47C]">
      24/7
    </p>

    <p className="text-xs text-slate-400 leading-relaxed mt-2 max-w-[220px]">
      A sua operação online sempre disponível.
    </p>
  </div>

  {/* ACESSO */}
  <div>
    <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mb-3">
      // ACESSO
    </p>

    <p className="text-2xl sm:text-3xl font-light tracking-tight text-white">
      Multi-plataforma
    </p>

    <p className="text-xs text-slate-400 leading-relaxed mt-2 max-w-[220px]">
      Web, Desktop e PWA.
    </p>
  </div>

  {/* PAGAMENTOS */}
  <div>
    <p className="text-[10px] text-slate-500 uppercase tracking-[0.2em] mb-3">
      // PAGAMENTOS
    </p>

    <p className="text-2xl sm:text-3xl font-light tracking-tight text-white">
      3 métodos
    </p>

    <p className="text-xs text-slate-400 leading-relaxed mt-2 max-w-[220px]">
      M-Pesa, e-Mola e Visa integrados.
    </p>
  </div>

</div>

        </div>
      </motion.section>

      {/* SECTION 2: STORYBOARD / MÓDULOS */}
      <section id="demostracao" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div>
            <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 02. FLUXO OPERACIONAL INTEGRADO</span>
            <h2 className="text-3xl sm:text-5xl font-light text-white mt-3">E se a sua loja física e a sua loja online trabalhassem como uma só?</h2>
          </div>
          <p className="text-slate-300 font-light max-w-md text-sm leading-relaxed">
            No MPV, produtos, clientes, pedidos e vendas podem viver no mesmo ecossistema. Venda presencialmente, receba pedidos online e acompanhe a operação sem trocar de sistema.
          </p>
        </div>

        {/* Console Interativo */}
        <div className="bg-[#12142B] border border-white/10 p-4 sm:p-8 rounded-none relative shadow-2xl">
          
          {/* Header do Terminal */}
          <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-6 font-mono text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 bg-red-500/80 rounded-full" />
              <span className="w-2.5 h-2.5 bg-yellow-500/80 rounded-full" />
              <span className="w-2.5 h-2.5 bg-green-500/80 rounded-full" />
              <span className="ml-4 text-slate-400 uppercase tracking-widest hidden sm:inline">LIVE MODULE PREVIEW</span>
            </div>
            <div className="text-[#52C47C] font-mono">
              [ MÓDULO: {steps[activeStep].id} ]
            </div>
          </div>

          {/* Display da Imagem / Animação */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center min-h-[480px]">
            
            <div className="lg:col-span-8 relative aspect-video bg-[#0B0C1E] overflow-hidden border border-white/10 group">
              <img 
                src={steps[activeStep].image} 
                alt={steps[activeStep].label}
                className="w-full h-full object-cover object-top opacity-90 transition-all duration-700 group-hover:scale-105"
              />
              <div className="absolute top-4 left-4 bg-[#0B0C1E]/90 backdrop-blur border border-white/20 font-mono text-[10px] text-[#52C47C] px-3 py-1 uppercase tracking-widest">
                STATUS: {steps[activeStep].tag}
              </div>
            </div>

            {/* Painel Lateral de Controle */}
            <div className="lg:col-span-4 flex flex-col justify-between h-full space-y-6">
              <div>
                <span className="font-mono text-xs text-[#5055A8] uppercase tracking-widest font-bold">MÓDULO {steps[activeStep].id}</span>
                <h3 className="text-2xl font-light text-white mt-2 mb-4">{steps[activeStep].headline}</h3>
                <p className="text-slate-300 text-sm font-light leading-relaxed">{steps[activeStep].sub}</p>
              </div>

              {/* Botões para alternar fases do storyboard */}
              <div className="space-y-2 pt-6 border-t border-white/10">
                {steps.map((step, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveStep(idx)}
                    className={`w-full text-left p-3 font-mono text-xs transition-all flex items-center justify-between border ${
                      activeStep === idx 
                        ? 'bg-[#52C47C] text-[#0B0C1E] border-[#52C47C] font-bold shadow-md' 
                        : 'bg-white/5 text-slate-300 border-white/5 hover:border-white/20 hover:text-white'
                    }`}
                  >
                    <span>{step.id}. {step.label}</span>
                    <ChevronRight className={`w-3.5 h-3.5 ${activeStep === idx ? 'text-[#0B0C1E]' : 'text-slate-500'}`} />
                  </button>
                ))}
              </div>
            </div>

          </div>

        </div>

      </section>

      {/* SECTION 3: INDÚSTRIAS */}
      <section id="funcionalidades" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        
        <div className="max-w-3xl mb-20">
          <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 03. ADAPTAÇÃO TOTAL AO SEU MODELO</span>
          <h2 className="text-3xl sm:text-6xl font-light text-white mt-4 leading-tight">
            O software é que deve entender o seu negócio.
          </h2>
          <p className="text-slate-300 text-lg font-light mt-6">
            Restaurantes não vendem como lojas. Lojas não operam como consultoras. Ginásios não gerem clientes como empresas de crédito. Por isso, o MPV adapta a experiência às necessidades de cada operação.
          </p>
        </div>

        {/* Grid de Nichos */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          
          {[
            {
              code: "MOD-01",
              title: "Restauração & Alimentação",
              desc: "POS, mesas, menu digital, pedidos e cozinha ligados numa só operação",
              tag: "Pedidos sem confusão."
            },
            {
              code: "MOD-02",
              title: "Retalho & Comércio Físico",
              desc: "POS, stock, produtos, clientes, pagamentos e catálogo online.",
              tag: "Venda rápido. Saiba o que tem."
            },
            {
              code: "MOD-03",
              title: "Moda & Estética",
              desc: "Catálogo digital, clientes, vendas, indicações e presença online.",
              tag: "Transforme seguidores em clientes."
            },
            {
              code: "MOD-04",
              title: "Serviços & B2B Corporativo",
              desc: "Captação, pipeline, propostas, clientes, facturação e acompanhamento comercial.",
              tag: "Pare de perder leads pelo caminho."
            },
            {
              code: "MOD-05",
              title: "Fitness & Subscrições",
              desc: "Membros, planos, pagamentos, renovações e vendas adicionais.",
              tag: "Menos mensalidades esquecidas."
            },
            {
              code: "MOD-06",
              title: "Microcrédito & Financeiras",
              desc: "Empréstimos, pagamentos, prestações, atrasos e histórico financeiro.",
              tag: "Controle cada operação."
            }
          ].map((item, idx) => (
            <div 
              key={idx}
              className="p-8 bg-[#12142B] border border-white/10 hover:border-[#52C47C]/50 transition-all duration-300 flex flex-col justify-between group"
            >
              <div>
                <div className="flex items-center justify-between font-mono text-xs text-slate-400 mb-6">
                  <span>{item.code}</span>
                  <span className="text-[#52C47C] border border-[#52C47C]/30 px-2 py-0.5">{item.tag}</span>
                </div>
                <h3 className="text-xl font-light text-white mb-4 group-hover:text-[#52C47C] transition-colors">{item.title}</h3>
                <p className="text-slate-300 text-sm font-light leading-relaxed">{item.desc}</p>
              </div>

              <div className="pt-8 mt-8 border-t border-white/5 flex items-center justify-between font-mono text-xs text-slate-400">
                <span>Explorar solução</span>
                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:translate-x-1 group-hover:text-[#52C47C] transition-all" />
              </div>
            </div>
          ))}

        </div>

      </section>

      {/* SECTION 4: O ECOSSISTEMA */}
      <section id="ecossistema" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        <div className="max-w-3xl mb-16">
          <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 04. O QUE MUDA QUANDO TUDO CONVERSA</span>
          <h2 className="text-3xl sm:text-6xl font-light text-white mt-4 leading-tight">O que muda quando tudo começa a conversar?</h2>
          <p className="text-slate-300 text-lg font-light mt-6">Menos ferramentas dispersas. Mais contexto em cada decisão — desde o primeiro contacto até à receita.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { n: "01", title: "Você sabe de onde vêm os clientes.", desc: "Capte leads e acompanhe a origem de cada oportunidade." },
            { n: "02", title: "Você sabe o que está a vender.", desc: "Produtos, serviços, pedidos e canais numa visão conectada." },
            { n: "03", title: "Você sabe quem está a comprar.", desc: "Histórico e informação dos clientes no mesmo ecossistema." },
            { n: "04", title: "Você sabe quanto está a ganhar.", desc: "Metas, receitas, comissões e rentabilidade para decidir melhor." },
            { n: "05", title: "Você sabe onde agir.", desc: "Transforme dados da operação em próximos passos concretos." }
          ].map((item) => (
            <div key={item.n} className="p-6 bg-[#12142B] border border-white/10 hover:border-[#52C47C]/50 transition-all duration-300">
              <span className="font-mono text-xs text-[#52C47C]">{item.n}</span>
              <h3 className="text-lg font-light text-white mt-5 mb-3">{item.title}</h3>
              <p className="text-slate-400 text-sm font-light leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 5: CAPTAÇÃO E PIPELINE */}
      <section id="captacao" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 05. CAPTAÇÃO → PIPELINE → CLIENTE</span>
            <h2 className="text-3xl sm:text-6xl font-light text-white mt-4 leading-tight">E se cada contacto interessado entrasse directamente no seu processo comercial?</h2>
            <p className="text-slate-300 text-lg font-light mt-6 leading-relaxed">Crie formulários personalizados para diferentes campanhas, canais ou produtos. Cada submissão pode entrar no seu pipeline para ser acompanhada até à conversão.</p>
            <div className="flex flex-wrap gap-2 mt-8 font-mono text-xs uppercase tracking-widest">
              {['FORMULÁRIO', 'LEAD', 'PIPELINE', 'FOLLOW-UP', 'CLIENTE'].map((step, index) => (
                <React.Fragment key={step}>
                  <span className="px-3 py-2 border border-[#52C47C]/30 bg-[#52C47C]/5 text-[#52C47C]">{step}</span>
                  {index < 4 && <span className="px-1 py-2 text-slate-500">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 bg-[#12142B] border border-white/10 p-8">
            <span className="font-mono text-xs text-slate-400 uppercase tracking-widest">// RESULTADO</span>
            <h3 className="text-2xl font-light text-white mt-4">Pare de deixar potenciais clientes perdidos pelo caminho.</h3>
            <p className="text-slate-400 text-sm leading-relaxed mt-4">WhatsApp, Instagram, formulários e contactos de campanha podem alimentar um processo comercial mais organizado.</p>
            <a href="#planos" className="inline-flex mt-8 px-6 py-4 bg-[#52C47C] text-[#0B0C1E] font-mono text-xs font-bold tracking-widest uppercase hover:bg-white transition-all">Ver como funciona <ChevronRight className="w-4 h-4 ml-2" /></a>
          </div>
        </div>
      </section>

      {/* SECTION 6: PLANOS */}
      <section id="planos" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10 relative">
        
        {/* Glow sutil futurista */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-[#52C47C]/5 blur-[160px] pointer-events-none" />

        <div className="text-center max-w-3xl mx-auto mb-16 relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#52C47C]/30 bg-[#52C47C]/10 font-mono text-xs text-[#52C47C] uppercase tracking-widest mb-6">
            <Terminal className="w-3.5 h-3.5" />
            <span>// 06. UMA CONDIÇÃO QUE NÃO VAI FICAR AQUI PARA SEMPRE</span>
          </div>

          <h2 className="text-4xl sm:text-6xl font-light text-white tracking-tight">
            E se a mensalidade simplesmente deixasse de existir?
          </h2>

          <p className="text-slate-300 text-lg font-light mt-6 leading-relaxed">
            Durante a campanha Founders Lifetime Pass, adquira o acesso ao MPV através de um único pagamento, sem renovação mensal da licença. Escolha o plano que melhor corresponde à escala da sua operação.
          </p>
        </div>

        {/* Tabela de Preços */}
        <div className="relative z-10">
          <PricingSection />
        </div>

        <div className="mt-12 p-6 bg-[#12142B] border border-white/10 text-center font-mono text-xs text-slate-300 max-w-2xl mx-auto">
          <p>⚡ <span className="text-white font-bold">CONFIGURAÇÃO INCLUÍDA:</span> A equipa do MPV auxilia na configuração inicial dos seus produtos, utilizadores e menus sem taxas de setup.</p>
           <p className="mt-3 text-slate-400">O acesso à plataforma é vitalício de acordo com o plano contratado e enquanto o serviço MPV estiver operacional. Serviços externos, integrações ou necessidades personalizadas podem ter custos adicionais quando solicitados.</p>
        </div>

      </section>

      {/* SECTION 7: CTA FINAL */}
      <section id="aderir" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 07. O PRÓXIMO PASSO É SEU</span>
          <h2 className="text-4xl sm:text-6xl font-light text-white mt-4 leading-tight">O seu negócio já está a funcionar. Agora faça o sistema trabalhar a seu favor.</h2>
          <p className="text-slate-300 text-lg font-light mt-6 leading-relaxed max-w-2xl mx-auto">Escolha o plano que melhor corresponde à sua operação e aproveite a condição Founders Lifetime Pass enquanto a campanha estiver disponível.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-10">
            <a href="#planos" className="px-8 py-5 bg-[#52C47C] text-[#0B0C1E] font-mono text-xs font-bold tracking-widest uppercase hover:bg-white transition-all flex items-center justify-center gap-3 group shadow-xl shadow-[#52C47C]/20">
              GARANTIR O MEU LIFETIME PASS
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a href="#demostracao" className="px-8 py-5 border border-white/15 text-white font-mono text-xs font-bold tracking-widest uppercase hover:border-[#52C47C] hover:text-[#52C47C] transition-all flex items-center justify-center">
              VER UMA DEMO
            </a>
          </div>
          <p className="font-mono text-[10px] text-slate-500 uppercase tracking-widest mt-6">A partir de 39.900 MT · Pagamento único · Sem mensalidade de licença</p>
        </div>
      </section>

      {/* SECTION 8: PERGUNTAS FREQUENTES (FAQ) */}
      <section id="faq" className="py-28 px-6 sm:px-12 max-w-7xl mx-auto border-b border-white/10">
        <div className="mb-16">
          <span className="font-mono text-xs text-[#52C47C] tracking-widest uppercase">// 08. ANTES DE COMEÇAR</span>
          <h2 className="text-3xl sm:text-5xl font-light text-white mt-3">Ainda está a pensar: “Mas será que o MPV é para mim?”</h2>
          <p className="text-slate-400 text-sm font-light mt-3 max-w-xl">
            As respostas às dúvidas mais comuns sobre o MPV, os planos e o Founders Lifetime Pass. Se ainda houver alguma questão, fale com a nossa equipa.
          </p>
        </div>

        <FAQSection />
      </section>

      {/* FOOTER */}
      <footer className="py-16 px-6 sm:px-12 max-w-7xl mx-auto font-mono text-xs text-slate-400">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 pb-12 border-b border-white/10">
          <div>
            <img 
              src="https://meupontodevenda.com/cdn/logo-white.svg" 
              alt="MPV — Meu Ponto de Venda" 
              className="h-6 w-auto object-contain opacity-90 mb-3"
            />
            <p className="text-slate-400">A sua empresa vende. O MPV organiza o resto.</p>
          </div>

          <div className="flex flex-wrap gap-8">
            <a href="#arquitetura" className="hover:text-white transition">Visão</a>
            <a href="#demostracao" className="hover:text-white transition">Ecossistema</a>
            <a href="#funcionalidades" className="hover:text-white transition">Soluções</a>
            <a href="#planos" className="hover:text-white transition">Planos</a>
            <a href="#aderir" className="hover:text-white transition">Aderir</a>
            <a href="#faq" className="hover:text-white transition">FAQ</a>
            <Link to="/terms" className="hover:text-white transition">Termos</Link>
            <Link to="/privacy" className="hover:text-white transition">Privacidade</Link>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-[10px] tracking-widest uppercase">
          <p>© {new Date().getFullYear()} MPV — MEU PONTO DE VENDA. DESENVOLVIDO EM MOÇAMBIQUE.</p>
          <div className="flex items-center gap-2 text-slate-400">
            <span className="w-2 h-2 rounded-full bg-[#52C47C]" />
            <span>SISTEMAS OPERACIONAIS EM MAPUTO, NAMPULA E BEIRA</span>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;